import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { useAtualizarOportunidade, useOportunidade } from '../hooks';
import {
  buildUpdatePayload,
  OpportunityForm,
  OpportunityFormState,
  stateFromOportunidade,
} from '../components/OpportunityForm';

/**
 * Página `/oportunidades/:id/editar` — edição de oportunidade
 * efetivada. Rascunhos não são editáveis por aqui (o backend rejeita
 * PATCH em rascunho; o fluxo de promoção fica para fatia dedicada).
 */
export default function EditarOportunidadePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: opp, isLoading, isError, error } = useOportunidade(id);
  const atualizar = useAtualizarOportunidade(id ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialState = useMemo(
    () => (opp ? stateFromOportunidade(opp) : undefined),
    [opp],
  );

  const submit = async (state: OpportunityFormState) => {
    if (!opp) return;
    setSubmitError(null);
    try {
      const payload = buildUpdatePayload(state, opp);
      if (Object.keys(payload).length === 0) {
        // Nada mudou — só volta pro detalhe. Evita gravar auditoria vazia.
        navigate(`/oportunidades/${opp.id}`);
        return;
      }
      await atualizar.mutateAsync(payload);
      navigate(`/oportunidades/${opp.id}`);
    } catch (err) {
      setSubmitError(getFriendlyErrorMessage(err, 'Falha ao atualizar oportunidade.'));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (isError || !opp) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate('/oportunidades')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para a lista
        </button>
        <Alert variant="error">
          {isError
            ? `Erro ao carregar oportunidade: ${(error as Error)?.message ?? 'Falha desconhecida.'}`
            : 'Oportunidade não encontrada ou sem permissão de visualização.'}
        </Alert>
      </div>
    );
  }

  if (opp.isDraft) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate(`/oportunidades/${opp.id}`)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para o detalhe
        </button>
        <Alert variant="warning">
          Este registro é um rascunho — a edição direta ainda não está disponível.
          O fluxo de promoção do rascunho entra em uma fatia frontend seguinte.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <button
        onClick={() => navigate(`/oportunidades/${opp.id}`)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para o detalhe
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Editar oportunidade{' '}
          <span className="font-mono text-lg text-gray-500">{opp.codigo}</span>
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Alterações em valor estimado, custo ou probabilidade invalidam qualquer
          aprovação de margem vigente.
        </p>
      </div>

      <OpportunityForm
        initialState={initialState}
        original={opp}
        onSubmit={submit}
        onCancel={() => navigate(`/oportunidades/${opp.id}`)}
        submitLabel="Salvar alterações"
        isSubmitting={atualizar.isPending}
        submitError={submitError}
      />
    </div>
  );
}
